import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FilterDropdown from "../../components/FilterDropdown";

import { NumericFilterEditor } from "./NumericFilterEditor";

// Analyse (Beta-Feedback 2026-10-07): 0 = alle Mehrkosten; Schwellen nach oben.
const MIN_DELTA_QUICK_VALUES = [0, 5, 15, 25] as const;

export function MinDeltaPctControl({
  value,
  onChange,
  rulePct,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  /** The Prüfregel threshold, named in the menu so people find it. */
  rulePct?: number;
}) {
  const isQuickValue =
    value !== null &&
    (MIN_DELTA_QUICK_VALUES as readonly number[]).includes(value);
  const selectedValue = value ?? 0;
  const [customValue, setCustomValue] = useState(
    value !== null && !isQuickValue ? String(value) : "",
  );
  const [customOpen, setCustomOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (value !== null && !isQuickValue) {
      setCustomValue(String(value));
    }
  }, [isQuickValue, rulePct, value]);

  const options = useMemo(() => {
    const items: { value: string; label: string }[] =
      MIN_DELTA_QUICK_VALUES.map((quick) => ({
        value: String(quick),
        label: quick === 0 ? "Alle Mehrkosten" : quick === rulePct ? `ab ${quick} % (Prüfregel)` : `ab ${quick} %`,
      }));
    if (value !== null && !isQuickValue) {
      items.push({ value: String(value), label: value === rulePct ? `ab ${value} % (Prüfregel)` : `ab ${value} %` });
    }
    items.push({ value: "custom", label: "Eigener Wert …" });
    return items;
  }, [isQuickValue, rulePct, value]);

  const closeCustomEditor = useCallback((restoreFocus: boolean) => {
    setCustomOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() =>
        anchorRef.current?.querySelector("button")?.focus(),
      );
    }
  }, []);

  return (
    <div ref={anchorRef} className="relative">
      <FilterDropdown
        label="Abweichung"
        ariaLabel="Mindestabweichung filtern"
        options={options}
        value={String(selectedValue)}
        defaultValue="0"
        showValue
        onChange={(next) => {
          if (next === "custom") {
            setCustomValue(value !== null && !isQuickValue ? String(value) : "");
            setCustomOpen(true);
            return;
          }
          const parsed = Number(next);
          onChange(parsed === 0 ? null : parsed);
        }}
      />
      <NumericFilterEditor
        open={customOpen}
        title="Eigener Wert in %"
        ariaLabel="Eigene Mindestabweichung festlegen"
        value={customValue}
        onValueChange={setCustomValue}
        onApply={(next) => {
          onChange(next);
          closeCustomEditor(true);
        }}
        onClose={closeCustomEditor}
        isValid={(next) => Number.isFinite(next) && next > 0}
        invalidMessage="Bitte einen Wert größer als 0 eingeben."
        min={0}
        step={5}
        placeholder="z. B. 30"
        suffix="%"
      />
    </div>
  );
}
